import React, { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { Section } from 'src/components-v2/Layout';
import styled from 'threads5/styles/styled';
import { VideoPlayerProps } from './VideoPlayer.types';

// helpers
import { Lazy } from 'src/components-v2/Helpers';
import { EMBEDDED_VIDEO_EVENTS } from 'src/lib/trackingEvents';
import { getUrlDomain } from 'src/lib/url-helpers';

// contexts
import { useTrack } from 'src/contexts/AnalyticsContext';

const supportedVideoProviderDomains = ['youtube.com', 'youtu.be', 'vimeo.com'];

const ReactPlayerWrapper = styled('div')({
  position: 'absolute',
  inset: 0,
});

const VideoPlayer = React.forwardRef(
  (
    {
      isLazy = true,
      url = '',
      width = '100%',
      height = '100%',
      light = '',
      playing = false,
      playIcon = null,
      muted = false,
      sx,
      tabIndex,
      ...rest
    }: VideoPlayerProps,
    ref,
  ) => {
    const playerRef = useRef<HTMLVideoElement>();
    const track = useTrack();
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
      if (!isReady || !playerRef.current || typeof tabIndex !== 'number') {
        return;
      }
      // find the iframe inside of wrapper
      const iframe = playerRef.current.shadowRoot?.querySelector('iframe');
      if (iframe) {
        iframe.tabIndex = tabIndex;
        iframe.contentWindow.focus();
      }
    }, [isReady, playing, tabIndex]);

    if (!supportedVideoProviderDomains.includes(getUrlDomain(url))) return null;

    return (
      <Section
        ref={ref}
        component='figure'
        sx={{
          position: 'relative',
          pt: '56.25%',
          height: '0',
          overflow: 'hidden',
          ...sx,
        }}
        {...rest}
      >
        <Lazy isLazy={isLazy} height={height}>
          {/* The testid lives on our wrapper because react-player v3 only
              forwards slot/className/style to the DOM. It also marks the
              player for Happo's waitFor: react-player mounts its UI through
              React.lazy, so this wrapper stays empty until that chunk loads. */}
          <ReactPlayerWrapper data-testid='video-embed'>
            <ReactPlayer
              ref={playerRef}
              src={url}
              playing={playing}
              width={width}
              height={height}
              light={light}
              controls={true}
              pip={true}
              playIcon={playIcon}
              playsInline={true}
              muted={muted}
              onReady={() => {
                setIsReady(true);
              }}
              onPlay={() => {
                track({
                  type: EMBEDDED_VIDEO_EVENTS.ON_PLAY,
                  payload: { sanitizedUrl: url },
                });
              }}
              onPause={() => {
                track({
                  type: EMBEDDED_VIDEO_EVENTS.ON_PAUSE,
                  payload: { sanitizedUrl: url },
                });
              }}
              onSeeking={() => {
                track({
                  type: EMBEDDED_VIDEO_EVENTS.ON_SEEK,
                  payload: { sanitizedUrl: url },
                });
              }}
              onEnded={() => {
                track({
                  type: EMBEDDED_VIDEO_EVENTS.ON_ENDED,
                  payload: { sanitizedUrl: url },
                });
              }}
              onError={() => {
                track({
                  type: EMBEDDED_VIDEO_EVENTS.ON_ERROR,
                  payload: { sanitizedUrl: url },
                });
              }}
            />
          </ReactPlayerWrapper>
        </Lazy>
      </Section>
    );
  },
);

export default VideoPlayer;
